import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import renderHTML from 'react-render-html';
import { Redirect } from 'react-router-dom';

import LinkButton from 'components/TrainingContent/LinkButton';

import APIHandler from 'config/APIHandler';
import StorageHandler from 'config/StorageHandler';

import { useChapters } from 'hooks/useChapters';
import { useSettings } from 'hooks/useSettings';

import RetainContent from 'views/RetainContentView/RetainContent';
import Sidebar from 'views/RetainContentView/Sidebar';

import { appContentUrl, storageKeys } from 'settings';

const RetainContentView = ({ match }) => {
  const { appId, loaded: settingsLoaded, lang } = useSettings();
  const { code, langFile } = lang;
  const { chapters, loaded: chaptersLoaded } = useChapters(appId);

  const [, setVerified] = useState(false);
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState(null);
  const [redirect, setRedirect] = useState(null);
  const [buttons, setButtons] = useState([]);

  const {
    url,
    params: { chapterId, pageId },
  } = useMemo(() => match, [match]);

  // handles switching pages with the arrow keys
  const switchPage = useCallback(
    (e) => {
      const i = items.map(({ index }) => index).indexOf(+pageId);
      let newRedirect = null;

      if (e.key === 'ArrowDown' && i < items.length - 1) {
        e.preventDefault();
        newRedirect = items[i + 1].index;
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        newRedirect = items[0].index;
      }
      if (e.key === 'ArrowUp' && i > 0) {
        e.preventDefault();
        newRedirect = items[i - 1].index;
      } else if (e.key === 'ArrowUp' && i === 0) {
        e.preventDefault();
        newRedirect = items[items.length - 1].index;
      }

      if (newRedirect !== null) {
        setVerified(false);
        setRedirect(`/main/ret/chapter/${chapterId}/${newRedirect}`);
      }
    },
    [chapterId, items, pageId],
  );

  // Use effect to manage the event listener (for switching pages on key presses)
  useEffect(() => {
    document.querySelector('html').addEventListener('keydown', switchPage);
    return () => {
      document.querySelector('html').removeEventListener('keydown', switchPage);
    };
  }, [switchPage]);

  // sets the buttons for the page
  const prepButtons = useCallback((page) => {
    const newButtons = [];
    let index = 1;

    if (page !== undefined) {
      index = 1;
      Object.keys(page).forEach((link) => {
        const links = [page[`link${index}`], page[`linkType${index}`]].every(
          (val) => val !== undefined && val.length > 0,
        );
        const linksValid = page.question === undefined && links;
        if (link === `link${index}` && linksValid) {
          newButtons.push(
            <LinkButton
              key={page[`linkType${index}`]}
              link={page[`link${index}`]}
              type={page[`linkType${index}`]}
              textHtml={page[`linkTextHtml${index}`]}
              pageType="retain"
            />,
          );
          index += 1;
        }
      });
      setButtons(newButtons);
    }
  }, []);

  // fetches page info from either the local storage or the API (if not available in local storage). If the pages are fetched from the API, they are saved to local storage.
  const getPages = useCallback(
    async () =>
      StorageHandler.load(storageKeys.pages).then((localPages) => {
        if (localPages) {
          const [final] = localPages.filter((page) => page.upID !== undefined);
          if (final && final.upID.indexOf(chapterId) === 0) return localPages;
        }
        return APIHandler.getTrainingPages(chapterId, `${langFile}-${langFile.toUpperCase()}`).then((newPages) => {
          StorageHandler.save(storageKeys.pages, newPages);
          return newPages;
        });
      }),
    [chapterId, code, langFile],
  );

  // creates the list of items for the sidebar (for navigation between pages)
  const setList = useCallback(async () => {
    if (!(settingsLoaded && chaptersLoaded)) return;

    const newPages = await getPages();

    const newItems = newPages
      .map((page, i) => ({
        name: page.title || page.pageType,
        type: page.pageType,
        active: page.upID === newPages[pageId].upID,
        index: i,
        onClick: () => {
          setVerified(false);
          setRedirect(`/main/retain/chapter/${chapterId}/${i}`);
        },
      }))
      .filter(({ type }) => type !== undefined && type !== 'Final-Page'); // Filter out any undefined pages (these are questions) and the final page (this is the "congradulations" page)

    prepButtons(newPages[+pageId]);
    setPages(newPages);
    setItems(newItems);
    setVerified(true);
  }, [settingsLoaded, chaptersLoaded, getPages, prepButtons, pageId, chapterId]);

  const verifyUrl = useCallback(() => {
    if (chaptersLoaded && pages) {
      const validPage =
        chapters.some((chapter) => Number(chapter.id) === Number(chapterId)) &&
        Number(pageId) >= 0 &&
        Number(pageId) < pages?.length;

      if (!validPage) {
        setRedirect(url.substring(0, url.indexOf(chapterId)));
      }
    }
  }, [chapterId, chapters, chaptersLoaded, pageId, pages, url]);

  useEffect(() => {
    setList();
  }, [setList]);

  if (!(settingsLoaded && chaptersLoaded)) return null;

  return (
    <section className="retain container">
      {items && chaptersLoaded && (
        <Sidebar appId={appId} items={items} image={chapters.find((chapter) => +chapter.id === +chapterId)?.image} />
      )}
      {pages && (
        <RetainContent
          appId={appId}
          image={`${appContentUrl}${appId}/${pages[pageId].image}`}
          summary={{
            title: pages[pageId].title,
            text: renderHTML(pages[pageId].summary),
          }}
          params={pages[pageId]}
          buttons={buttons}
        />
      )}
      {verifyUrl()}
      {redirect && redirect !== url && <Redirect to={redirect} />}{' '}
      {/* Redirect functionality controlled by the 'redirect' state */}
    </section>
  );
};

RetainContentView.propTypes = {
  match: PropTypes.shape({
    url: PropTypes.string.isRequired,
    params: PropTypes.shape({
      chapterId: PropTypes.string.isRequired,
      pageId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default RetainContentView;

// --------
// TODO: Legacy code below. Can be removed after final codecheck.
// --------

// const verify = () =>
//   // If the user does not exist, redirect. Is behavior handled internally in the useUserInfo hook, which is calleed by the useChapters hook
//   StorageHandler.load(storageKeys.user).then((user) => {
//     if (!user) return <Redirect to="/login" />;
//     return StorageHandler.load(storageKeys.chapters).then((chapters) => {
//       if (!chapters) return <Redirect to="/main/retain" />;
//       return { value: true, chapters };
//     });
//   });

// const getAppInfo = async () => {
//   const appId = await DataHandler.matchAppId();
//   const appInfo = await DataHandler.checkKey(storageKeys.chapters, APIHandler.getChapters(appId));

//   return appInfo || {};
// };

// class RetainContentViewOld extends Component {
//   constructor(props) {
//     super(props);

//     this.state = {
//       appId: -1,
//       verified: false,
//       chapters: null,
//       pages: null,
//       items: [],
//       buttons: [],
//       redirect: null,
//     };

//     this.switchPage = this.switchPage.bind(this);
//     this.getAppInfo = this.getAppInfo.bind(this);
//   }

//   componentDidMount() {
//     document.querySelector('html').addEventListener('keydown', this.switchPage);

//     this.initList();
//     getAppInfo(); // NOTE: Anonymous localStorage side-effect, this should be more explicit or just remove altogether
//   }

//   componentDidUpdate() {
//     const { verified } = this.state;
//     if (!verified) {
//       this.setList();
//     }
//   }

//   componentWillUnmount() {
//     document.querySelector('html').removeEventListener('keydown', this.switchPage);
//   }

//   async getAppInfo() {
//     const appId = await DataHandler.matchAppId();
//     await DataHandler.checkKey(storageKeys.chapters, APIHandler.getChapters(appId));
//   }

//   getPages() {
//     // const { chapterId } = this.props.match.params;
//     const {
//       match: {
//         params: { chapterId },
//       },
//     } = this.props;

//     return StorageHandler.load(storageKeys.pages).then((localPages) => {
//       if (localPages) {
//         const [final] = localPages.filter((page) => page.upID !== undefined);
//         if (final && final.upID.indexOf(chapterId) === 0) return localPages;
//       }
//       return APIHandler.getTrainingPages(chapterId).then((pages) => {
//         StorageHandler.save(storageKeys.pages, pages);
//         return pages;
//       });
//     });
//   }

//   async setList() {
//     // const { chapterId, pageId } = this.props.match.params;
//     const {
//       match: {
//         params: { chapterId, pageId },
//       },
//     } = this.props;
//     const pages = await this.getPages();

//     console.log('setListPages', pages);

//     const items = pages
//       .map((page, i) => ({
//         name: page.title || page.pageType,
//         type: page.pageType,
//         active: page.upID === pages[pageId].upID,
//         index: i,
//         onClick: () => {
//           this.setState({ verified: false, redirect: `/main/retain/chapter/${chapterId}/${i}` });
//         },
//       }))
//       .filter(({ type }) => type !== undefined && type !== 'Final-Page');

//     this.setButtons(pages[+pageId]);
//     this.setState({
//       verified: true,
//       items,
//       pages,
//     });
//   }

//   setButtons(page) {
//     console.log('setButtonsOld page', page);
//     const buttons = [];
//     let index = 1;

//     if (page !== undefined) {
//       index = 1;
//       Object.keys(page).forEach((link) => {
//         const links = [page[`link${index}`], page[`linkType${index}`]].every(
//           (val) => val !== undefined && val.length > 0,
//         );
//         const linksValid = page.question === undefined && links;
//         if (link === `link${index}` && linksValid) {
//           buttons.push(
//             <LinkButton
//               key={page[`linkType${index}`]}
//               link={page[`link${index}`]}
//               type={page[`linkType${index}`]}
//               textHtml={page[`linkTextHtml${index}`]}
//             />,
//           );
//           index += 1;
//         }
//       });
//       this.setState({ buttons });
//     }
//   }

//   async initList() {
//     const appId = await DataHandler.matchAppId();
//     const verified = await verify();
//     const { value, chapters } = verified;

//     this.setState({
//       appId,
//       verified: value || verified,
//       chapters: chapters || null,
//     });

//     this.setList();
//   }

//   switchPage(e) {
//     const { match } = this.props;
//     const { pageId, chapterId } = match.params;
//     const { items } = this.state;

//     const i = items.map(({ index }) => index).indexOf(+pageId);
//     let redirect = null;

//     e.preventDefault();
//     if (e.key === 'ArrowDown' && i < items.length - 1) {
//       redirect = items[i + 1].index;
//     } else if (e.key === 'ArrowDown') {
//       redirect = items[0].index;
//     }
//     if (e.key === 'ArrowUp' && i > 0) {
//       redirect = items[i - 1].index;
//     } else if (e.key === 'ArrowUp' && i === 0) {
//       redirect = items[items.length - 1].index;
//     }

//     if (redirect !== null) this.setState({ verified: false, redirect: `/main/ret/chapter/${chapterId}/${redirect}` });
//   }

//   verifyUrl() {
//     const { match } = this.props;
//     const { chapters, pages } = this.state;

//     const { url, params } = match;
//     const { pageId, chapterId } = params;

//     if (chapters && pages) {
//       const validPage =
//         chapters.app.some((chapter) => Number(chapter.chapterID) === Number(chapterId)) &&
//         Number(pageId) >= 0 &&
//         Number(pageId) < pages.length;

//       if (!validPage) {
//         return <Redirect to={url.substring(0, url.indexOf(chapterId))} />;
//       }
//     }
//     return null;
//   }

//   render() {
//     const { match } = this.props;
//     const { appId, verified, items, pages, chapters, buttons, redirect } = this.state;

//     const {
//       url,
//       params: { chapterId, pageId },
//     } = match;

//     return (
//       <section className="retain container">
//         {verified !== true ? verified : null} {/* Why? */}
//         {items && chapters && (
//           <Sidebar
//             appId={appId}
//             items={items}
//             image={chapters.app.filter((chapter) => +chapter.chapterID === +chapterId)[0].chapterImage}
//           />
//         )}
//         {pages && (
//           <RetainContent
//             appId={appId}
//             image={`${appContentUrl}${appId}/${pages[pageId].image}`}
//             summary={{
//               title: pages[pageId].title,
//               text: renderHTML(pages[pageId].summary),
//             }}
//             params={pages[pageId]}
//             buttons={buttons}
//           />
//         )}
//         {this.verifyUrl()}
//         {redirect && redirect !== url && <Redirect to={redirect} />}
//       </section>
//     );
//   }
// }

// RetainContentViewOld.propTypes = {
//   match: PropTypes.shape().isRequired,
// };
